import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { api, formatGBP } from "@/lib/api";
import { toast } from "sonner";

const SHIPPING_OPTIONS = [
  { value: "standard", label: "Standard Delivery", days: "3-5 business days", price: 5.0 },
  { value: "express", label: "Express Delivery", days: "1-2 business days", price: 12.0 },
];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("standard");
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, full_name: user.name, email: user.email }));
  }, [user]);

  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items, navigate]);

  const shippingPrice = shippingMethod === "standard" && subtotal >= 200 ? 0 : SHIPPING_OPTIONS.find(o => o.value === shippingMethod).price;
  const total = subtotal + shippingPrice;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          length: i.length,
          texture: i.texture,
          color: i.color,
        })),
        shipping: form,
        shipping_method: shippingMethod,
        origin_url: window.location.origin,
      };
      const { data } = await api.post("/checkout/session", payload);
      // Persist order_id locally so success page knows
      localStorage.setItem("hb_last_order", data.order_id);
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="bg-[#FFF4E0] min-h-screen" data-testid="checkout-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-16">
        <div className="overline mb-3">Step 2 of 2</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-[#1A0F11] mb-12">Checkout</h1>

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 space-y-10">
            <section>
              <h2 className="font-serif-display text-2xl text-[#1A0F11] mb-6">Contact</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="label-luxe">Full name</div>
                  <input required name="full_name" value={form.full_name} onChange={onChange} className="input-luxe" data-testid="checkout-fullname" />
                </div>
                <div>
                  <div className="label-luxe">Email</div>
                  <input required type="email" name="email" value={form.email} onChange={onChange} className="input-luxe" data-testid="checkout-email" />
                </div>
                <div>
                  <div className="label-luxe">Phone</div>
                  <input required name="phone" value={form.phone} onChange={onChange} className="input-luxe" data-testid="checkout-phone" />
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif-display text-2xl text-[#1A0F11] mb-6">Shipping Address</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <div className="label-luxe">Address line 1</div>
                  <input required name="address_line1" value={form.address_line1} onChange={onChange} className="input-luxe" data-testid="checkout-address1" />
                </div>
                <div className="md:col-span-2">
                  <div className="label-luxe">Address line 2 (optional)</div>
                  <input name="address_line2" value={form.address_line2} onChange={onChange} className="input-luxe" data-testid="checkout-address2" />
                </div>
                <div>
                  <div className="label-luxe">City</div>
                  <input required name="city" value={form.city} onChange={onChange} className="input-luxe" data-testid="checkout-city" />
                </div>
                <div>
                  <div className="label-luxe">Postcode</div>
                  <input required name="postcode" value={form.postcode} onChange={onChange} className="input-luxe" data-testid="checkout-postcode" />
                </div>
                <div className="md:col-span-2">
                  <div className="label-luxe">Country</div>
                  <input required name="country" value={form.country} onChange={onChange} className="input-luxe" data-testid="checkout-country" />
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-serif-display text-2xl text-[#1A0F11] mb-6">Delivery</h2>
              <div className="space-y-3">
                {SHIPPING_OPTIONS.map((o) => {
                  const free = o.value === "standard" && subtotal >= 200;
                  return (
                    <label key={o.value} className={`flex items-center justify-between border p-4 cursor-pointer transition ${shippingMethod === o.value ? "border-[#831132] bg-[#FDF1EC]" : "border-[#E8D8C8] hover:border-[#831132]/50"}`} data-testid={`shipping-${o.value}`}>
                      <div className="flex items-center gap-4">
                        <input type="radio" name="shipping" checked={shippingMethod === o.value} onChange={() => setShippingMethod(o.value)} className="accent-[#831132]" />
                        <div>
                          <div className="text-sm font-medium text-[#1A0F11]">{o.label}</div>
                          <div className="text-xs text-[#5C4A4D]">{o.days}</div>
                        </div>
                      </div>
                      <div className="text-sm font-medium text-[#1A0F11]">{free ? "Free" : formatGBP(o.price)}</div>
                    </label>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-[#FDF1EC] p-8 sticky top-32">
              <h3 className="font-serif-display text-2xl text-[#1A0F11] mb-6">Order Summary</h3>
              <div className="space-y-4 max-h-72 overflow-y-auto pr-2">
                {items.map((it) => (
                  <div key={it.key} className="flex gap-3">
                    <img src={it.image_url} alt={it.name} className="w-16 h-20 object-cover" />
                    <div className="flex-1 text-sm">
                      <div className="text-[#1A0F11]">{it.name}</div>
                      <div className="text-xs text-[#5C4A4D]">Qty {it.quantity}{it.length ? ` · ${it.length}` : ""}</div>
                    </div>
                    <div className="text-sm font-medium">{formatGBP(it.price * it.quantity)}</div>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#E8D8C8] mt-6 pt-6 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-[#5C4A4D]">Subtotal</span><span>{formatGBP(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-[#5C4A4D]">Shipping</span><span>{shippingPrice === 0 ? "Free" : formatGBP(shippingPrice)}</span></div>
                <div className="flex justify-between font-serif-display text-2xl text-[#1A0F11] pt-3"><span>Total</span><span data-testid="checkout-total">{formatGBP(total)}</span></div>
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full mt-6" data-testid="checkout-pay-button">
                {submitting ? "Redirecting..." : "Pay Securely"}
              </button>
              <p className="text-xs text-[#5C4A4D] mt-3 text-center">You will be redirected to Stripe to complete payment.</p>
            </div>
          </aside>
        </form>
      </div>
    </div>
  );
}
